import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import {
  Menu,
  X,
  Home,
  Package,
  Newspaper,
  Info,
  Phone,
  Search,
  ChevronRight,
  ShoppingBag,
  LogInIcon,
  Zap,
  Users,
  Plus,
  User,
  LogOut,
  Settings,
  Bell,
  FileText,
  Shield,
  LogIn,
  UserPlus,
  HelpCircle,
  Star,
  Award,
  ArrowRight,
  Loader2,
} from "lucide-react";
import ThemeToggle from "../common/ThemeToggle";
import SearchBar from "../common/SearchBar";
import { productService } from "../../api/services/productService";
import { adminLogout, userLogout } from "../../store/slices/authSlice";

const Header = ({ darkMode, setDarkMode }) => {
  const dispatch = useDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { user, admin, isAuthenticated, isAdmin } = useSelector(
    (state) => state.auth,
  );

  const navItems = [
    {
      path: "/",
      label: "Home",
      icon: <Home size={16} />,
      description: "Home Page",
    },
    {
      path: "/products",
      label: "Products",
      icon: <Package size={16} />,
      description: "Browse our catalog",
    },
    {
      path: "/blog",
      label: "Blog",
      icon: <Newspaper size={16} />,
      description: "Latest insights",
    },
    {
      path: "/about",
      label: "About",
      icon: <Info size={16} />,
      description: "Our mission",
    },
    {
      path: "/contact",
      label: "Contact",
      icon: <Phone size={16} />,
      description: "Get in touch",
    },
  ];

  // Helper function to get full image URL
  const getFullImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    if (path.startsWith("/uploads")) {
      const apiBaseUrl =
        import.meta.env.VITE_API_URL || "http://localhost:3000";
      return `${apiBaseUrl}${path}`;
    }
    return path;
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search products with debounce
  const searchProducts = (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await productService.getAllProducts({
          search: query,
          limit: 5,
          page: 1,
        });

        let products = [];
        if (response?.data?.data?.products) {
          products = response.data.data.products;
        } else if (response?.data?.products) {
          products = response.data.products;
        } else if (response?.products) {
          products = response.products;
        } else if (Array.isArray(response?.data?.data)) {
          products = response.data.data;
        } else if (Array.isArray(response?.data)) {
          products = response.data;
        }

        setSearchResults(products.slice(0, 5));
        setShowResults(true);
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleSearch = (query) => {
    const searchQuery = query?.trim() || mobileSearchQuery?.trim();
    if (searchQuery) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setIsMenuOpen(false);
      setIsSearchOpen(false);
      setMobileSearchQuery("");
      setShowResults(false);
      setSearchResults([]);
    }
  };

  const handleMobileSearchChange = (e) => {
    const value = e.target.value;
    setMobileSearchQuery(value);
    searchProducts(value);
  };

  const handleProductClick = (product) => {
    navigate(`/products/${product.slug || product.id}`);
    setIsSearchOpen(false);
    setIsMenuOpen(false);
    setMobileSearchQuery("");
    setShowResults(false);
    setSearchResults([]);
  };

  const handleLogout = () => {
    if (isAdmin) {
      dispatch(adminLogout());
    } else {
      dispatch(userLogout());
    }
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const getDisplayName = () => {
    if (admin) return `${admin.firstName} ${admin.lastName}`;
    if (user) return `${user.firstName} ${user.lastName}`;
    return "";
  };

  const getInitials = () => {
    if (admin)
      return `${admin.firstName?.[0] || ""}${admin.lastName?.[0] || ""}`;
    if (user) return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`;
    return "U";
  };

  const getRoleBadge = () => {
    if (isAdmin) {
      return {
        label: "Admin",
        className:
          "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
      };
    }
    return {
      label: "Verified",
      className:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
    };
  };

  const roleBadge = getRoleBadge();

  // Mobile Search Dropdown Component - No search button, 100% responsive
  const MobileSearchDropdown = () => (
    <div ref={searchRef} className="relative">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch(mobileSearchQuery);
        }}
        className="flex items-center bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 focus-within:border-primary-500 dark:focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all duration-300 shadow-sm"
      >
        <Search size={18} className="ml-3 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          autoFocus
          value={mobileSearchQuery}
          onChange={handleMobileSearchChange}
          onFocus={() => setShowResults(true)}
          placeholder="Search products..."
          className="flex-1 px-3 py-3 bg-transparent border-none focus:outline-none text-gray-900 dark:text-white placeholder-gray-400 text-sm"
        />
        {isSearching && (
          <Loader2
            size={16}
            className="mr-3 animate-spin text-gray-400 flex-shrink-0"
          />
        )}
        {mobileSearchQuery && !isSearching && (
          <button
            type="button"
            onClick={() => {
              setMobileSearchQuery("");
              setSearchResults([]);
              setShowResults(false);
            }}
            className="mr-2 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
          >
            <X size={16} className="text-gray-400" />
          </button>
        )}
      </form>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {showResults && mobileSearchQuery.trim() && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden z-[60]"
          >
            <div className="max-h-80 overflow-y-auto">
              {searchResults.length > 0 ? (
                <>
                  <div className="p-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      Search Results ({searchResults.length})
                    </p>
                  </div>
                  {searchResults.map((product) => {
                    const imageUrl = product.mainImagePath
                      ? getFullImageUrl(product.mainImagePath)
                      : product.galleryImages?.[0]?.path
                        ? getFullImageUrl(product.galleryImages[0].path)
                        : null;

                    return (
                      <button
                        key={product.id}
                        onClick={() => handleProductClick(product)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                      >
                        {/* Product Image */}
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 relative">
                          {imageUrl ? (
                            <>
                              <img
                                src={imageUrl}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Package size={18} className="text-gray-400" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={18} className="text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {product.category === "food"
                              ? "Food & Beverages"
                              : "Non-Food Products"}
                          </p>
                        </div>

                        {/* Arrow */}
                        <ChevronRight
                          size={16}
                          className="text-gray-400 flex-shrink-0"
                        />
                      </button>
                    );
                  })}
                </>
              ) : !isSearching ? (
                <div className="p-6 text-center">
                  <Package size={32} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No products found</p>
                </div>
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-50 flex justify-center pt-4"
      >
        <div className="w-[95%] md:w-[90%] max-w-7xl">
          <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-800/30 shadow-sm">
            <div className="px-4 py-1.5">
              <div className="flex items-center justify-between gap-2">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2 group shrink-0">
                  <div className="relative">
                    <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-md rounded-md shadow-md group-hover:shadow-lg transition-all border border-white/50 dark:border-gray-700/50 flex items-center justify-center w-9 h-9 sm:w-7 sm:h-7">
                      <img
                        src="/Icon.png"
                        alt="TradeBridge Logo"
                        className="w-7 h-7 sm:w-5 sm:h-5 object-contain"
                      />
                    </div>
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  </div>
                  <div className="hidden sm:block">
                    <span className="text-sm font-semibold">
                      <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                        Trade
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        Bridge
                      </span>
                    </span>
                    <p className="text-[9px] text-gray-500 dark:text-gray-400 tracking-wide">
                      Bridging Suppliers with Markets
                    </p>
                  </div>
                </Link>

                {/* Desktop Navigation */}
                <nav className="hidden lg:flex items-center gap-1">
                  {navItems.map((item) => (
                    <div key={item.path} className="relative group">
                      <Link
                        to={item.path}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 inline-block ${
                          location.pathname === item.path
                            ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                            : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          {item.icon}
                          {item.label}
                        </span>
                      </Link>
                    </div>
                  ))}
                </nav>

                {/* Right Side Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsSearchOpen(!isSearchOpen)}
                    className={`lg:hidden p-1.5 rounded-lg transition-colors ${
                      isSearchOpen
                        ? "bg-primary-100 dark:bg-primary-900/30 text-primary-600"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <Search size={14} />
                  </button>

                  <div className="hidden lg:block w-64 relative z-50">
                    <SearchBar
                      onSearch={handleSearch}
                      placeholder="Search products..."
                      size="small"
                      darkMode={darkMode}
                    />
                  </div>

                  <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />

                  {isAuthenticated && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/inquiry")}
                      className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-br from-primary-500 to-secondary-500 text-white text-xs font-medium rounded-lg hover:shadow-lg transition-all"
                    >
                      <Plus size={12} />
                      <span>New Request</span>
                    </motion.button>
                  )}

                  {/* User Menu */}
                  {isAuthenticated ? (
                    <div className="relative">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                        className="flex items-center gap-1.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                      >
                        <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-md flex items-center justify-center text-white text-xs font-bold shadow-md">
                          {getInitials()}
                        </div>
                        <span className="hidden md:block text-xs font-medium text-gray-700 dark:text-gray-300 pr-1">
                          {getDisplayName().split(" ")[0]}
                        </span>
                      </motion.button>

                      <AnimatePresence>
                        {isUserMenuOpen && (
                          <>
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              onClick={() => setIsUserMenuOpen(false)}
                              className="fixed inset-0 z-40"
                            />
                            <motion.div
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 10, scale: 0.95 }}
                              className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden z-50"
                            >
                              <div className="p-3 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg">
                                    {getInitials()}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                      {getDisplayName()}
                                    </p>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                      {isAdmin
                                        ? "Administrator"
                                        : "Customer Account"}
                                    </p>
                                    <span
                                      className={`inline-block mt-1 px-1.5 py-0.5 text-[9px] font-medium rounded-full ${roleBadge.className}`}
                                    >
                                      {roleBadge.label}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="p-2">
                                {isAdmin ? (
                                  <>
                                    <MenuItem
                                      icon={<Shield size={14} />}
                                      label="Dashboard"
                                      to="/admin"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                    <MenuItem
                                      icon={<Settings size={14} />}
                                      label="Settings"
                                      to="/admin/settings"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                    <MenuItem
                                      icon={<Users size={14} />}
                                      label="Users Management"
                                      to="/admin/admins"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <MenuItem
                                      icon={<User size={14} />}
                                      label="My Profile"
                                      to="/user/profile"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                    <MenuItem
                                      icon={<FileText size={14} />}
                                      label="My Requests"
                                      to="/user/requests"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                    <MenuItem
                                      icon={<Bell size={14} />}
                                      label="Notifications"
                                      to="/user/notifications"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                    <MenuItem
                                      icon={<Settings size={14} />}
                                      label="Settings"
                                      to="/user/settings"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    />
                                  </>
                                )}
                                <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                                <MenuItem
                                  icon={<HelpCircle size={14} />}
                                  label="Help & Support"
                                  to="/contact"
                                  onClick={() => setIsUserMenuOpen(false)}
                                />
                                <button
                                  onClick={handleLogout}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all mt-1"
                                >
                                  <LogOut size={14} />
                                  <span>Logout</span>
                                </button>
                              </div>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="hidden md:flex items-center gap-1.5">
                      <Link
                        to="/user/login"
                        className="px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-1 group"
                      >
                        <span>Login</span>
                        <ArrowRight
                          size={12}
                          className="group-hover:translate-x-0.5 transition-transform"
                        />
                      </Link>
                    </div>
                  )}

                  <button
                    onClick={() => setIsMenuOpen(true)}
                    className="lg:hidden p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <Menu size={16} />
                  </button>
                </div>
              </div>

              {/* Mobile Search Bar with Dropdown */}
              <AnimatePresence>
                {isSearchOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="lg:hidden mt-2 pb-2"
                  >
                    <MobileSearchDropdown />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              style={{ backdropFilter: "blur(4px)" }}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white dark:bg-gray-900 shadow-2xl z-50 lg:hidden"
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 sm:w-7 sm:h-7 rounded-md overflow-hidden flex items-center justify-center">
                      <img
                        src="/Icon.png"
                        alt="Logo"
                        className="w-7 h-7 sm:w-full sm:h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 dark:text-white">
                        TradeBridge
                      </p>
                      <p className="text-[9px] text-gray-500">
                        Bridging Suppliers with Markets
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Search in Mobile Menu with Dropdown */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                  <MobileSearchDropdown />
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-1">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                          location.pathname === item.path
                            ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`p-1.5 rounded-md ${location.pathname === item.path ? "bg-primary-100" : "bg-gray-100 dark:bg-gray-800"}`}
                          >
                            {item.icon}
                          </div>
                          <div>
                            <p className="text-xs font-medium">{item.label}</p>
                            <p className="text-[9px] text-gray-500">
                              {item.description}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                      </Link>
                    ))}
                  </div>

                  {!isAuthenticated && (
                    <div className="mt-6 space-y-2">
                      <Link
                        to="/user/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-1.5 w-full p-2 bg-primary-50 text-primary-600 rounded-lg text-xs"
                      >
                        <LogIn size={14} />
                        <span className="font-medium">Login</span>
                      </Link>
                      <Link
                        to="/user/register"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-center gap-1.5 w-full p-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg text-xs"
                      >
                        <UserPlus size={14} />
                        <span className="font-medium">Create Account</span>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const MenuItem = ({ icon, label, to, onClick, badge }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all group"
  >
    <div className="flex items-center gap-2">
      {icon}
      <span>{label}</span>
    </div>
    {badge && (
      <span className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">
        {badge}
      </span>
    )}
  </Link>
);

export default Header;
